import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.PROD ?
    'https://geo-agent-j28t.onrender.com' :
    '');

export interface AdminDashboardData {
  subscriptions: {
    total: number;
    plans: {
      name: string;
      tier: string | null;
      interval: string;
      count: number;
      amountCents: number;
      mrr: number;
    }[];
    totalMrr: number;
  };
  costs: {
    last30Days: {
      day: string;
      calls: number;
      cost: number;
      profiles: number;
    }[];
    totalLast30Days: number;
    byProvider: {
      provider: string;
      totalCost: number;
      calls: number;
      avgCostPerCall: number;
    }[];
    byContext: {
      context: string;
      totalCost: number;
      calls: number;
    }[];
  };
  summary: {
    projectedMonthlyRevenue: number;
    projectedAnnualRevenue: number;
    monthlyLlmCost: number;
    profitMargin: number;
  };
  topCostlyUsers: {
    profileId: string;
    email: string | null;
    totalCost: number;
    calls: number;
    firstSeen: string;
  }[];
  modelUsage: {
    modelId: string;
    provider: string;
    totalCost: number;
    calls: number;
    inputTokens: number;
    outputTokens: number;
    avgCostPerCall: number;
  }[];
  dailyModelCosts: {
    day: string;
    modelId: string;
    provider: string;
    cost: number;
    calls: number;
  }[];
  dailyProviderCosts: {
    day: string;
    provider: string;
    cost: number;
    calls: number;
  }[];
  queryStats: {
    totalQueries: number;
    queriesLast30Days: number;
    mentionRate: number;
    bySource: { source: string; count: number }[];
    dailyQueries: { day: string; count: number }[];
  };
  usersStats: {
    totalUsers: number;
    newThisMonth: number;
    activeUsers30d: number;
    totalResources: number;
  };
}

export interface AdminResource {
  id: string;
  url: string;
  domain: string | null;
  retestEnabled: boolean;
  lastReTestedAt: string | null;
  hasLlmsTxt: boolean;
  retestExcludedQueries: string[];
}

export interface AdminResourceQuery {
  query: string;
  lastTestedAt: string | null;
  mentionsWebsite: boolean;
}

export interface AdminPerson {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  isDisabled: boolean;
  createdAt: string;
  subscription: {
    status: string;
    plan: string | null;
    cancelAtPeriodEnd: boolean;
  } | null;
  resources: AdminResource[];
}

export interface AdminPeopleResponse {
  people: AdminPerson[];
  total: number;
  page: number;
  limit: number;
}

export const getAdminPeople = async (
  apiKey: string,
  search?: string,
  page = 1,
  limit = 20,
): Promise<AdminPeopleResponse> => {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  params.set('page', String(page));
  params.set('limit', String(limit));
  const url = `${API_BASE_URL}/api/admin/people?${params.toString()}`;
  const response = await axios.get(url, {
    headers: { 'X-API-Key': apiKey },
  });
  return response.data;
};

export const toggleProfileDisabled = async (apiKey: string, profileId: string): Promise<{ isDisabled: boolean }> => {
  const url = `${API_BASE_URL}/api/admin/people/${profileId}/disable`;
  const response = await axios.patch(url, {}, {
    headers: { 'X-API-Key': apiKey },
  });
  return response.data;
};

export const toggleResourceRetest = async (apiKey: string, resourceId: string): Promise<{ retestEnabled: boolean }> => {
  const url = `${API_BASE_URL}/api/admin/resources/${resourceId}/retest`;
  const response = await axios.patch(url, {}, {
    headers: { 'X-API-Key': apiKey },
  });
  return response.data;
};

export const cancelUserSubscription = async (apiKey: string, profileId: string): Promise<{ success: boolean }> => {
  const url = `${API_BASE_URL}/api/admin/people/${profileId}/cancel-subscription`;
  const response = await axios.post(url, {}, {
    headers: { 'X-API-Key': apiKey },
  });
  return response.data;
};

export const getResourceQueries = async (
  apiKey: string,
  resourceId: string,
): Promise<{ queries: AdminResourceQuery[]; excludedQueries: string[] }> => {
  const url = `${API_BASE_URL}/api/admin/resources/${resourceId}/queries`;
  const response = await axios.get(url, {
    headers: { 'X-API-Key': apiKey },
  });
  return response.data;
};

export const toggleQueryRetestAdmin = async (
  apiKey: string,
  resourceId: string,
  query: string,
  enabled: boolean,
): Promise<{ excludedQueries: string[] }> => {
  const url = `${API_BASE_URL}/api/admin/resources/${resourceId}/toggle-query`;
  const response = await axios.patch(url, { query, enabled }, {
    headers: { 'X-API-Key': apiKey },
  });
  return response.data;
};

// ─── Report Types ───

export interface ReportQueryItem {
  query: string;
  explanation: string;
}

export interface ReportCategory {
  name: string;
  queries: ReportQueryItem[];
}

export interface GenerateReportQueriesResponse {
  businessName: string;
  domain: string;
  categories: ReportCategory[];
}

export interface ReportResultItem {
  category: string;
  query: string;
  responseText: string;
  mentioned: boolean;
  businessNameFound: boolean;
  urlFound: boolean;
  citations: Array<{ url: string; title: string; text: string }>;
}

export interface ExecuteReportResponse {
  results: ReportResultItem[];
}

export const generateReportQueries = async (
  apiKey: string,
  websiteUrl: string,
): Promise<GenerateReportQueriesResponse> => {
  const url = `${API_BASE_URL}/api/admin/reports/generate-queries`;
  const response = await axios.post(url, { websiteUrl }, {
    headers: { 'X-API-Key': apiKey },
  });
  return response.data;
};

export const executeVisibilityReport = async (
  apiKey: string,
  websiteUrl: string,
  businessName: string,
  queries: Array<{ category: string; query: string }>,
): Promise<ExecuteReportResponse> => {
  const url = `${API_BASE_URL}/api/admin/reports/execute`;
  const response = await axios.post(url, { websiteUrl, businessName, queries }, {
    headers: { 'X-API-Key': apiKey },
  });
  return response.data;
};

export const getAdminDashboard = async (apiKey: string, start?: string, end?: string): Promise<AdminDashboardData> => {
  const params = new URLSearchParams();
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  const qs = params.toString();
  const url = `${API_BASE_URL}/api/admin/dashboard${qs ? `?${qs}` : ''}`;
  const response = await axios.get(url, {
    headers: { 'X-API-Key': apiKey },
  });
  return response.data;
};
